import { OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    MatLegacyPaginatorIntl as MatPaginatorIntl,
    MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg';
import { IntercomConfig, IntercomModule } from 'ng-intercom';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { NgxPendoModule } from 'ngx-pendo';
import {
    PERFECT_SCROLLBAR_CONFIG,
    PerfectScrollbarConfigInterface,
} from 'ngx-perfect-scrollbar';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ToastrModule } from 'ngx-toastr';
import { RouteReuseStrategy } from '@angular/router';
import { CustomRouteReuseStrategy } from './core/route-reuse-strategy/custom-route-reuse.strategy';
import { NavigationReloadService } from './core/services/navigation-reload.service';

import { HttpUtilsService, TypesUtilsService } from '@app/core/_base/crud';
import {
    KtDialogService,
    LayoutConfigService,
    LayoutRefService,
    MenuAsideService,
    MenuConfigService,
    MenuHorizontalService,
    PageConfigService,
    SplashScreenService,
    SubheaderService,
} from '@app/core/_base/layout';
import { CNCMatPaginatorIntl } from '@app/core/_common/cncMatPaginatorIntl';
import { LayoutConfig } from '@app/core/_config/channics-sidebar/layout.config';
import { AuthGuard, JwtInterceptor } from '@app/core/auth';
import { PermissionService } from '@app/core/auth/services/permission.service';
import { CoreModule } from '@app/core/core.module';
import { metaReducers, reducers } from '@app/core/reducers';
import { environment } from '@app/env-service';
import { SidebarLayoutModule } from '@app/layout-sidebar/layout.module';
import { PartialsModule } from '@app/layout-sidebar/partials/partials.module';
import { AuthModule } from '@app/modules/auth/auth.module';
import { CustomFieldModule } from '@app/modules/custom-field/custom-field.module';
import { LocationComponent } from '@app/modules/locations/location/location.component';
import { WarehouseMobileAppModule } from '@app/modules/warehouse-mobile-app/warehouse-mobile-app.module';
import { PrintLetterTemplateComponent } from '@app/prints/print-letter-template/print-letter-template.component';
import { PrintProductComponent } from '@app/prints/print-product/print-product.component';
import { OverridesComponent } from '@app/shared/components/overrides/overrides.component';
import { SvgOutletComponent } from '@app/shared/components/svg-outlet/svg-outlet.component';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { GlobalSearchModule } from './core/components/global-search/global-search.module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { EmbedReplitModule } from './modules/embed-replit/embed-replit.module';

//

const DEFAULT_PERFECT_SCROLLBAR_CONFIG :PerfectScrollbarConfigInterface = {
  wheelSpeed: 0.5,
  swipeEasing: true,
  minScrollbarLength: 40,
  maxScrollbarLength: 300,
};

export function initializeProviderFactory(
  appConfig :LayoutConfigService,
  featureToggleService :FeatureToggleService,
  permissionsService :PermissionService,
) {
  // initialize app by loading default demo layout config
  return async () => {
    if (appConfig.getConfig() === null) {
      appConfig.loadConfigs(new LayoutConfig().configs);
    }
    await environment.init();
    await featureToggleService.initSdk('init');
    // wait for permissions
    await permissionsService.getRbac();
  };
}

//

@NgModule({
  declarations: [
    AppComponent,
    PrintLetterTemplateComponent,
    PrintProductComponent,
    LocationComponent,
    SvgOutletComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    CoreModule,
    OverlayModule,
    AuthModule,
    // ThemeModule,
    SidebarLayoutModule,
    PartialsModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: true,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
    }),
    EffectsModule.forRoot([]),
    StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
    StoreDevtoolsModule.instrument(),
    NgbModule,
    TranslateModule.forRoot(),
    MatProgressSpinnerModule,
    InlineSVGModule.forRoot(),
    ToastrModule.forRoot(),
    HighlightModule,
    FormsModule,
    WarehouseMobileAppModule,
    CustomFieldModule,
    IntercomModule.forRoot({
      appId: 'b345by52',
      updateOnRouterChange: true, // will automatically run `update` on router event changes. Default: `false`
      rep_ai_call_us_extension: true, // note: service bell rebranded to rep ai
    } as IntercomConfig),
    NgxPendoModule.forRoot({
      pendoApiKey: 'f15652f4-cef0-469e-6527-ee7fe303ea3d',
      pendoIdFormatter: (value :any) => value.toString().toLowerCase(),
    }),
    MatTabsModule,
    MatPaginatorModule,
    NgxPermissionsModule.forRoot(),
    OverridesComponent,
    ReactiveFormsModule,
    EmbedReplitModule,
    GlobalSearchModule,
  ],
  exports: [],
  providers: [
    AuthGuard,
    LayoutConfigService,
    LayoutRefService,
    MenuConfigService,
    PageConfigService,
    KtDialogService,
    SplashScreenService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      // layout config initializer
      provide: APP_INITIALIZER,
      useFactory: initializeProviderFactory,
      deps: [LayoutConfigService, FeatureToggleService, PermissionService],
      multi: true,
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          typescript: () => import('highlight.js/lib/languages/typescript'),
          scss: () => import('highlight.js/lib/languages/scss'),
          xml: () => import('highlight.js/lib/languages/xml'),
          json: () => import('highlight.js/lib/languages/json'),
        },
      },
    },
    {
      provide: MatPaginatorIntl,
      useClass: CNCMatPaginatorIntl,
      deps: [TranslateService],
    },
    // template services
    SubheaderService,
    MenuHorizontalService,
    MenuAsideService,
    HttpUtilsService,
    TypesUtilsService,
    NavigationReloadService,
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
